/**
 * VerticalMode - Vertical scrolling reader mode
 * Location: /app/frontend/src/components/page-builder/reader/modes/
 * 
 * Phase 3-B: Enhanced with progress tracking, scroll restoration, and HTML sanitization
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useReaderContext, loadProgress } from '../hooks/useReaderContext';
import { sanitizeHtml, textToHtml } from '../hooks/sanitizeHtml';
import { Loader2, ImageOff, FileText, ChevronUp } from 'lucide-react';
import { Button } from '../../../ui/button';

const VerticalMode = () => {
  const { 
    content, 
    metadata, 
    isLoading, 
    error, 
    contentType,
    contentId,
    unitId,
    setProgress,
    progress,
  } = useReaderContext();
  
  const containerRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);

  // Handle scroll progress tracking
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const maxScroll = scrollHeight - clientHeight;
    const percent = maxScroll > 0 ? Math.round((scrollTop / maxScroll) * 100) : 0;
    
    setProgress({ 
      percent: Math.min(100, Math.max(0, percent)),
      scrollY: scrollTop,
    }, true);
    
    setShowScrollTop(scrollTop > 500);
  }, [setProgress]);

  // Restore scroll position on mount
  useEffect(() => {
    if (!isLoading && content && !hasRestoredScroll) {
      const saved = loadProgress(contentType, contentId, unitId, 'vertical');
      if (saved?.scrollY && containerRef.current) {
        // Slight delay to ensure content is rendered
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = saved.scrollY;
            setHasRestoredScroll(true);
          }
        }, 100);
      } else {
        setHasRestoredScroll(true);
      }
    }
  }, [isLoading, content, contentType, contentId, unitId, hasRestoredScroll]);

  // Attach scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Scroll to top
  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <ImageOff className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <FileText className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400">No content available</p>
      </div>
    );
  }

  // Comic: render vertical list of images/pages
  if (contentType === 'comic' && content.pages) {
    return (
      <div 
        ref={containerRef}
        className="vertical-reader w-full h-[calc(100vh-56px)] overflow-y-auto overflow-x-hidden"
        data-testid="vertical-mode-comic"
      >
        <div className="max-w-4xl mx-auto">
          {content.pages.map((page, index) => (
            <div 
              key={page.id || index} 
              className="page-container"
              data-page-index={index}
            >
              {page.image_url || page.url || page.src ? (
                <img
                  src={page.image_url || page.url || page.src}
                  alt={`Page ${index + 1}`}
                  className="w-full h-auto block"
                  loading={index < 3 ? 'eager' : 'lazy'}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`w-full aspect-[2/3] bg-zinc-800 flex items-center justify-center ${page.image_url || page.url || page.src ? 'hidden' : ''}`}>
                <ImageOff className="w-12 h-12 text-zinc-600" />
              </div>
            </div>
          ))}
          
          {/* End marker */}
          <div className="text-center py-8 text-zinc-500 border-t border-zinc-800 mt-4">
            <p className="text-sm">End of Episode</p>
            {metadata?.nextEpisodeId && (
              <p className="text-orange-400 mt-2">Next episode available</p>
            )}
          </div>
        </div>
        
        {/* Scroll to top button */}
        {showScrollTop && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-20 right-4 z-40 rounded-full w-12 h-12 bg-orange-500 hover:bg-orange-600 shadow-lg"
            size="icon"
          >
            <ChevronUp className="w-6 h-6" />
          </Button>
        )}
      </div>
    );
  }

  // Novel: render chapter text with HTML sanitization
  if (contentType === 'novel' && (content.text || content.contentHtml)) {
    // Determine content to render
    let htmlContent = '';
    if (content.contentHtml) {
      // Use provided HTML content (sanitize it)
      htmlContent = sanitizeHtml(content.contentHtml);
    } else if (content.text) {
      // Check if text contains HTML
      const hasHtml = /<[a-z][\s\S]*>/i.test(content.text);
      if (hasHtml) {
        htmlContent = sanitizeHtml(content.text);
      } else {
        htmlContent = textToHtml(content.text);
      }
    }

    return (
      <div 
        ref={containerRef}
        className="vertical-reader w-full h-[calc(100vh-56px)] overflow-y-auto overflow-x-hidden"
        data-testid="vertical-mode-novel"
      >
        <div className="max-w-3xl mx-auto px-6 py-8">
          <article 
            className="prose prose-invert prose-lg max-w-none prose-p:text-zinc-300 prose-p:leading-relaxed prose-p:mb-6 prose-headings:text-white prose-a:text-orange-400 prose-strong:text-white prose-blockquote:border-orange-500 prose-blockquote:text-zinc-400"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
          
          {/* Word count footer */}
          <div className="text-center py-8 text-zinc-500 border-t border-zinc-800 mt-8">
            <p className="text-sm">{content.wordCount?.toLocaleString() || '~'} words</p>
            <p className="text-xs text-zinc-600 mt-1">Progress: {progress.percent}%</p>
            {metadata?.nextChapterId && (
              <p className="text-orange-400 mt-2">Next chapter available</p>
            )}
          </div>
        </div>
        
        {/* Scroll to top button */}
        {showScrollTop && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-20 right-4 z-40 rounded-full w-12 h-12 bg-orange-500 hover:bg-orange-600 shadow-lg"
            size="icon"
          >
            <ChevronUp className="w-6 h-6" />
          </Button>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
      <FileText className="w-16 h-16 text-zinc-600 mb-4" />
      <p className="text-zinc-400">Unable to display content in vertical mode</p>
    </div>
  );
};

export default VerticalMode;
